import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";

// ── Phone Numbers ───────────────────────────────────────────────────────────
export type PhoneNumber = {
  id: string;
  number: string;
  formatted_number?: string;
  label?: string;
  provider?: "twilio" | "imported" | "custom" | string;
  status: "active" | "released" | "inactive" | string;
  inbound_agent?: string | null;
  inbound_agent_name?: string | null;
  /** When set, this Assistant answers inbound calls instead of the agent. */
  inbound_assistant?: string | null;
  inbound_assistant_name?: string | null;
  total_calls?: number;
  /** Calls in the log that point at this number — missed and failed ones
   * included, which `total_calls` leaves out. */
  linked_calls?: number;
  /** False once the number has call history: deleting the row would cut those
   * calls loose from the number they were made on. */
  can_delete?: boolean;
};

export type AvailableNumber = {
  number: string;
  formatted_number?: string;
  country_code?: string;
  capabilities?: { voice: boolean; sms: boolean };
};

export type ExistingNumber = {
  number: string;
  formatted_number?: string;
  sid?: string;
  already_imported?: boolean;
};

export const phoneNumbersApi = {
  list: async () => {
    const res = await api.get<Paginated<PhoneNumber> | PhoneNumber[]>(
      "/phone-numbers/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: {
    number: string;
    formatted_number?: string;
    label?: string;
    provider?: string;
    status?: string;
    inbound_agent?: string | null;
  }) => {
    const res = await api.post<PhoneNumber>("/phone-numbers/", data);
    return res.data;
  },
  update: async (
    id: string,
    data: {
      inbound_agent?: string | null;
      inbound_assistant?: string | null;
      provider?: string;
    },
  ) => {
    const res = await api.patch<PhoneNumber>(`/phone-numbers/${id}/`, data);
    return res.data;
  },
  search: async (data: { country_code?: string }) => {
    const res = await api.post<{ numbers: AvailableNumber[] }>(
      "/phone-numbers/search/",
      data,
    );
    return res.data;
  },
  existing: async () => {
    const res = await api.get<{ numbers: ExistingNumber[] }>(
      "/phone-numbers/existing/",
    );
    return res.data;
  },
  importNumber: async (data: { number: string; sid?: string }) => {
    const res = await api.post<PhoneNumber>("/phone-numbers/import/", data);
    return res.data;
  },
  purchase: async (data: { number: string }) => {
    const res = await api.post<PhoneNumber>("/phone-numbers/purchase/", data);
    return res.data;
  },
  release: async (id: string) => {
    await api.post(`/phone-numbers/${id}/release/`);
  },
  /** Remove the row entirely. Refused by the server (409) once the number has
   * calls in the log. */
  remove: async (id: string) => {
    await api.delete(`/phone-numbers/${id}/`);
  },
};

// ── Outbound Calls ──────────────────────────────────────────────────────────
export type OutboundCallInput = {
  // Exactly one of agent/assistant places the call.
  agent?: string | null;
  assistant?: string | null;
  from_number?: string | null;
  to_number: string;
  context?: string;
};

export type OutboundCallStatus =
  | "pending"
  | "queued"
  | "in_progress"
  | "completed"
  | "failed"
  | "cancelled";

export type OutboundCall = {
  id: string;
  agent: string | null;
  agent_name: string | null;
  assistant: string | null;
  assistant_name: string | null;
  to_number: string;
  status: OutboundCallStatus;
  error_message: string;
  created_at: string;
  call: {
    id: string;
    status: "pending" | "in_progress" | "completed" | "failed" | "missed" | "voicemail";
    error_message: string;
    duration_seconds: number;
    started_at: string | null;
    can_hangup: boolean;
  } | null;
};

export const outboundCallsApi = {
  create: async (data: OutboundCallInput) => {
    const res = await api.post<OutboundCall>("/telephony/outbound/", data);
    return res.data;
  },
  get: async (id: string) => {
    const res = await api.get<OutboundCall>(`/telephony/outbound/${id}/`);
    return res.data;
  },
  hangup: async (id: string) => {
    const res = await api.post(`/telephony/outbound/${id}/hangup/`);
    return res.data;
  },
};

// ── SIP Trunks ──────────────────────────────────────────────────────────────
export type SipTrunk = {
  id: string;
  name: string;
  description: string;
  phone_number: string | null;
  phone_number_display: string | null;
  termination_uri: string;
  sip_username: string;
  transport: "tcp" | "udp" | "tls";
  allowed_inbound_ips: string[];
  codecs: string[];
  max_concurrent_calls: number;
  status: "active" | "inactive";
  total_calls: number;
  created_at: string;
};

export type SipTrunkPayload = {
  name: string;
  description?: string;
  phone_number?: string | null;
  termination_uri: string;
  sip_username?: string;
  sip_password?: string;
  transport?: "tcp" | "udp" | "tls";
  allowed_inbound_ips?: string[];
  codecs?: string[];
  max_concurrent_calls?: number;
  status?: "active" | "inactive";
};

export const sipTrunksApi = {
  list: async () => {
    const res = await api.get<Paginated<SipTrunk> | SipTrunk[]>(
      "/phone-numbers/sip-trunks/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: SipTrunkPayload) => {
    const res = await api.post<SipTrunk>("/phone-numbers/sip-trunks/", data);
    return res.data;
  },
  update: async (id: string, data: Partial<SipTrunkPayload>) => {
    const res = await api.patch<SipTrunk>(
      `/phone-numbers/sip-trunks/${id}/`,
      data,
    );
    return res.data;
  },
  remove: async (id: string) => {
    await api.delete(`/phone-numbers/sip-trunks/${id}/`);
  },
  testConnection: async (id: string) => {
    const res = await api.post<{ ok: boolean; message: string; errors?: string[] }>(
      `/phone-numbers/sip-trunks/${id}/test_connection/`,
    );
    return res.data;
  },
  toggleStatus: async (id: string) => {
    await api.post(`/phone-numbers/sip-trunks/${id}/toggle_status/`);
  },
};

// ── WhatsApp Business Calling ───────────────────────────────────────────────
export type WhatsAppConfig = {
  id: string;
  phone_number_id: string;
  display_number: string;
  agent: string | null;
  agent_name: string | null;
  /** When set, this Assistant answers WhatsApp calls instead of the agent. */
  assistant: string | null;
  assistant_name: string | null;
  verify_token: string;
  is_active: boolean;
  token_set: boolean;
  secret_set: boolean;
  created_at: string;
};

export type WhatsAppConfigPayload = {
  phone_number_id: string;
  display_number?: string;
  agent?: string | null;
  assistant?: string | null;
  verify_token?: string;
  is_active?: boolean;
  whatsapp_token?: string;
  app_secret?: string;
};

export const whatsappApi = {
  list: async () => {
    const res = await api.get<Paginated<WhatsAppConfig> | WhatsAppConfig[]>(
      "/phone-numbers/whatsapp/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: WhatsAppConfigPayload) => {
    const res = await api.post<WhatsAppConfig>("/phone-numbers/whatsapp/", data);
    return res.data;
  },
  update: async (id: string, data: Partial<WhatsAppConfigPayload>) => {
    const res = await api.patch<WhatsAppConfig>(
      `/phone-numbers/whatsapp/${id}/`,
      data,
    );
    return res.data;
  },
  remove: async (id: string) => {
    await api.delete(`/phone-numbers/whatsapp/${id}/`);
  },
};

// ── Messaging settings (per-tenant) ─────────────────────────────────────────
export type MessagingSettings = {
  sms_sender_id: string;
  messaging_profile_id: string;
};

export const messagingSettingsApi = {
  get: async () => {
    const res = await api.get<MessagingSettings>("/tenants/messaging/");
    return res.data;
  },
  update: async (data: Partial<MessagingSettings>) => {
    const res = await api.patch<MessagingSettings>("/tenants/messaging/", data);
    return res.data;
  },
};
